import { useState, useEffect } from "react";
import { X, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import dapi from "../../lib/driverApi";
import { inr, fmtDate } from "../../lib/format";
import { Capacitor } from "@capacitor/core";
import { Checkout } from "capacitor-razorpay";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// Reusable payment sheet. kind: deposit | daily | outstanding
// Tomorrow's date in IST (YYYY-MM-DD) - earliest date allowed for the 2nd half payment
const tomorrowIST = () => new Date(Date.now() + 5.5 * 3600 * 1000 + 24 * 3600 * 1000).toISOString().slice(0, 10);

export default function PayModal({ open, onClose, kind, title, amount, lines = [], driverName, driverPhone, onDone, extraParams = {}, askNextDate = false, dismissible = true, allowZero = false, createOrderFn = null, children = null, payDisabled = false }) {
  const [nextDate, setNextDate] = useState("");
  const [phase, setPhase] = useState("confirm"); // confirm | processing | success
  const [result, setResult] = useState(null);

  // Nothing to pay (amount is 0) and no payment just completed -> close the popup.
  useEffect(() => {
    if (open && phase === "confirm" && kind !== "deposit" && !allowZero && !(Number(amount) > 0)) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, amount, phase, kind]);

  if (!open) return null;
  if (phase === "confirm" && kind !== "deposit" && !allowZero && !(Number(amount) > 0)) return null;

  const close = () => { setPhase("confirm"); setResult(null); onClose(); };

  const pay = async () => {
    if (askNextDate && (!nextDate || nextDate < tomorrowIST())) {
      return toast.error("Select a date for the next half payment (from tomorrow).");
    }
    setPhase("processing");
    try {
      const order = createOrderFn
        ? await createOrderFn()
        : (await dapi.post("/driver/payments/create-order", { kind, ...extraParams, ...(askNextDate ? { next_payment_date: nextDate } : {}) })).data;
      let verifyRes;
      if (order.gateway === "razorpay") {
        if (Capacitor.isNativePlatform()) {
          verifyRes = await new Promise(async (resolve, reject) => {
            try {
              const data = await Checkout.open({
                key: order.key_id, amount: order.amount_paise, currency: "INR",
                name: "MyEVRental", description: title, order_id: order.order_id,
                prefill: { name: driverName, contact: driverPhone },
                theme: { color: "#10b981" }
              });
              
              let resObj = data.response;
              if (typeof resObj === "string") {
                try { resObj = JSON.parse(resObj); } catch(e) {}
              }
              
              const verifyData = await dapi.post("/driver/payments/verify", {
                payment_id: order.payment_id,
                razorpay_order_id: resObj.razorpay_order_id,
                razorpay_payment_id: resObj.razorpay_payment_id,
                razorpay_signature: resObj.razorpay_signature,
              });
              resolve(verifyData.data);
            } catch (err) {
              reject(new Error(err.message || "cancelled"));
            }
          });
        } else {
          const ok = await loadRazorpay();
          if (!ok) throw new Error("Could not load payment gateway");
          verifyRes = await new Promise((resolve, reject) => {
            const rz = new window.Razorpay({
              key: order.key_id, amount: order.amount_paise, currency: "INR",
              name: "MyEVRental", description: title, order_id: order.order_id,
              prefill: { name: driverName, contact: driverPhone },
              theme: { color: "#10b981" },
              handler: async (res) => {
                try {
                  const { data } = await dapi.post("/driver/payments/verify", {
                    payment_id: order.payment_id,
                    razorpay_order_id: res.razorpay_order_id,
                    razorpay_payment_id: res.razorpay_payment_id,
                    razorpay_signature: res.razorpay_signature,
                  });
                  resolve(data);
                } catch (e) { reject(e); }
              },
              modal: { ondismiss: () => reject(new Error("cancelled")) },
            });
            rz.open();
          });
        }
      } else {
        // Sandbox: gateway not yet configured. Confirmation is still server-verified via a
        // server-issued token (never a frontend success flag). Plug in Razorpay keys to go live.
        const { data } = await dapi.post("/driver/payments/verify", {
          payment_id: order.payment_id, sandbox_token: order.sandbox_token,
        });
        verifyRes = data;
      }
      setResult(verifyRes);
      setPhase("success");
      toast.success("Payment successful ✓");
      onDone && onDone(verifyRes);
    } catch (e) {
      setPhase("confirm");
      if (e.message !== "cancelled") toast.error(e.response?.data?.detail || "Payment failed. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="pay-modal">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={phase !== "processing" && kind !== "deposit" && (dismissible || phase === "success") ? close : undefined} />
      <div className="relative w-full sm:max-w-sm max-h-[90vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl animate-[rise_.25s_ease]">
        {phase !== "processing" && kind !== "deposit" && (dismissible || phase === "success") && (
          <button onClick={close} data-testid="pay-close" className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        )}

        {phase === "success" ? (
          <div className="text-center py-2" data-testid="pay-success">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">Payment Confirmed</h3>
            <div className="text-3xl font-extrabold text-slate-900 mt-2">{inr(result?.amount || amount)}</div>
            <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-left space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Transaction ID</span><span className="font-semibold text-slate-800" data-testid="pay-txn">{result?.transaction_id}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Date &amp; Time</span><span className="font-medium text-slate-800">{fmtDate(result?.paid_at, true)}</span></div>
              {result?.account && <div className="flex justify-between"><span className="text-slate-500">Rental Status</span><span className="font-semibold text-emerald-600 uppercase">{result.account.status}</span></div>}
            </div>
            {kind === "outstanding" && result?.account?.status === "active" && (
              <div className="mt-4 text-sm text-emerald-600 font-semibold space-y-1" data-testid="pay-reactivated">
                <div>✓ Payment Confirmed</div>
              </div>
            )}
            <button onClick={close} data-testid="pay-continue" className="mt-5 w-full h-12 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold transition-colors">Continue</button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-bold text-slate-900">{title}</h3>
            <div className="mt-4 rounded-2xl bg-slate-50 p-4 space-y-2.5 text-sm">
              {lines.map((l, i) => (
                <div key={i} className={`flex justify-between ${l.strong ? "text-slate-900 font-bold text-base pt-2 border-t border-slate-200" : "text-slate-600"}`}>
                  <span>{l.label}</span><span>{l.value}</span>
                </div>
              ))}
            </div>
            {children}
            <button onClick={pay} disabled={phase === "processing" || payDisabled || (askNextDate && !nextDate)} data-testid="pay-now-btn"
              className="mt-5 w-full h-12 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-70">
              {phase === "processing" ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : <>Pay {inr(amount)}</>}
            </button>
            {askNextDate && (
              <div className="mt-4" data-testid="pay-next-date">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Select date for next half payment</label>
                <input type="date" min={tomorrowIST()} value={nextDate} onChange={(e) => setNextDate(e.target.value)}
                  className="w-full h-11 rounded-xl border border-slate-200 px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                <div className="text-[11px] text-slate-400 mt-1">Pick a date from tomorrow onwards to enable the Pay button.</div>
              </div>
            )}
            <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5" /> Secured payment · confirmed by server
            </div>
          </>
        )}
      </div>
    </div>
  );
}
